import { Injectable } from '@angular/core';
import { DbContext, DbTable } from '../data/dbcontext.service';
import { HttpClient } from '@angular/common/http';
import { DateService } from '../date/date.service';
import { DateParserService } from '../daily-log/daily-log-time/date-parser-service';
import { PtLuxonDate } from '../../models/projects/date.models';
import {
  WeatherReading,
  WeatherReadingCreationModel,
  WeatherRemarkEditor,
  WeatherSetup
} from '../../models/weather-readings/weather-readings.models';
import { OperationService } from '../operations/operation/operation.service';
import { SetupService } from '../setup/setup.service';
import { TimeZoneService } from '../time-zone/time-zone.service';
import { cloneDeep } from 'lodash';
import { AppConfig } from '../../app/app.config';

@Injectable()
export class WeatherReadingsService {
  config = AppConfig.settings;
  private readonly weatherLogDataTable: DbTable;
  private readonly weatherSetupTable: DbTable;
  private readonly weatherRemarkDataTable: DbTable;

  constructor(
    private http: HttpClient,
    private dbContext: DbContext,
    private dateService: DateService,
    private dateParserService: DateParserService,
    private operationService: OperationService,
    private setupService: SetupService,
    private timeZoneService: TimeZoneService
  ) {
    this.weatherLogDataTable = this.dbContext.tables.find(t => t.name === 'WeatherLogData');
    this.weatherSetupTable = this.dbContext.tables.find(t => t.name === 'WeatherSetup');
    this.weatherRemarkDataTable = this.dbContext.tables.find(t => t.name === 'WeatherRemarkData');
  }

  async getWeatherLogDataForDay(date: PtLuxonDate): Promise<WeatherReading[]> {
    let readings = await this.dbContext.getAll(this.weatherLogDataTable) as WeatherReading[];
    readings = await this.dateParserService.deserializeDates(readings, true, this.timeZoneService);

    let weatherReadingData: WeatherReading[] = [];

    for (const reading of readings) {
      const sameDay = await this.dateService.compareDayOffsetsPreApplied(date.dateTime, reading.date);
      if (sameDay) {
        weatherReadingData.push(reading);
      }
    }

    weatherReadingData = weatherReadingData.sort(function (a, b) {
      return a.date.toMillis() - b.date.toMillis();
    });

    if (weatherReadingData) {
      return weatherReadingData;
    }

    weatherReadingData = [];

    return weatherReadingData;
  }

  async getWeatherSetupsForDay(date?: PtLuxonDate): Promise<WeatherSetup[]> {
    // changes made ON the current log day
    let setupChangeData = await this.setupService.getSetupChangeLogDataForDay(date);
    setupChangeData = setupChangeData.filter(e => e.changeTypeId === 1);

    // need to find if there is a previous change - unless the current change on this day was made at exactly 00:00
    if (!setupChangeData || setupChangeData.length === 0 ||
      (setupChangeData && setupChangeData[0] && setupChangeData[0].timeOfChangeToShow !== '00:00')) {
      const previousChange = await this.setupService.getPreviousSetupChangeLogRecord(date, 1);
      if (previousChange) {
        setupChangeData.push(previousChange);
      }
    }

    // sort setupChangedata so that earlier changes come first, as this affects the project order of the kendo tabstrip
    setupChangeData = setupChangeData.sort(function (a, b) {
      return a.timeOfChange.dateTime.toMillis() - b.timeOfChange.dateTime.toMillis();
    });

    const projectIds = setupChangeData.map(e => e.newValueId);

    let weatherSetups = await this.dbContext.getAll(this.weatherSetupTable) as WeatherSetup[];
    weatherSetups = await this.dateParserService.deserializeDates(weatherSetups, true, this.timeZoneService);

    let weatherSetupData = [];

    for (let i = 0; i < projectIds.length; i++) {
      const setupIndex = weatherSetups.findIndex(w => w.projectId === projectIds[i]);

      if (setupIndex !== -1) {
        weatherSetupData.push(weatherSetups[setupIndex]);
      }
    }

    if (!!weatherSetupData) {
      return weatherSetupData;
    }

    weatherSetupData = [];

    return weatherSetupData;
  }

  async getWeatherRemarkEditorForDay(date: PtLuxonDate, projectId?: number): Promise<WeatherRemarkEditor> {
    let remarks = await this.dbContext.getAll(this.weatherRemarkDataTable) as WeatherRemarkEditor[];
    let results: WeatherRemarkEditor[];

    if (!remarks || remarks.length === 0) {
      return null;
    }

    remarks = await this.dateParserService.deserializeDates(remarks, true, this.timeZoneService);

    // todo is this needed ? use th dateTime offest
    // getting offsets to all remark times, need to do this before the next filter as the .filter function cannot handle the async await
    results = await Promise.all(remarks.map(async e => {
      const timeZone = await this.timeZoneService.getEditorLocally({dateTime: e.date, dateAmbiguous: true});

      e.dateOffset = timeZone
        ? (timeZone.secondsOffsetFromUtc / 60 / 60)
        : 0;

      return e;
    }));

    if (projectId) {
      results = remarks
        .filter(p => p.projectId === projectId && this.dateService
          .compareDayOffsetsPreApplied(date.dateTime, p.date));
    } else {
      results = remarks
        .filter(p => this.dateService
          .compareDayOffsetsPreApplied(date.dateTime, p.date));
    }

    const result = results[0];

    return result;
  }

  async deleteLocalWeatherReadingLogRecord(id: number) {
    await this.dbContext.remove(this.weatherLogDataTable, id);
  }

  deleteAllWeatherReadingRecords() {
    return this.dbContext.removeAll(this.weatherLogDataTable);
  }

  async saveWeatherReadingMultipleRecords(records: WeatherReading[]) {
    for (let i = 0; i < records.length; i++) {
      records[i] = await this.dateParserService.removeOffsetFromLuxonDates(records[i]);
      await this.dbContext.addOrUpdate(this.weatherLogDataTable, records[i].id || records[i].localId, records[i]);
    }
  }

  async getNewWeatherReadingLocalId() {
    return await this.dbContext.getTempId(this.weatherLogDataTable) as number;
  }

  deleteAllWeatherSetupRecords() {
    return this.dbContext.removeAll(this.weatherSetupTable);
  }

  async saveWeatherSetupMultipleRecords(records: WeatherSetup[]) {
    for (let i = 0; i < records.length; i++) {
      await this.dbContext.addOrUpdate(this.weatherSetupTable, records[i].id, records[i]);
    }
  }

  deleteAllWeatherRemarkEditors() {
    return this.dbContext.removeAll(this.weatherRemarkDataTable);
  }

  async saveWeatherRemarkMultipleEditors(editors: WeatherRemarkEditor[]) {
    for (let i = 0; i < editors.length; i++) {
      await this.dbContext.addOrUpdate(this.weatherRemarkDataTable, editors[i].id, editors[i]);
    }
  }

  setStatuses(weatherReadingData: WeatherReading[]): Promise<WeatherReading[]> {
    const url = `${this.config.apiUrl}api/weatherreading/getupdatedstatuses`;

    return this.http
      .post<WeatherReading[]>(url, weatherReadingData)
      .toPromise()
      .then((data: WeatherReading[]) => {
        return data;
      });
  }

  // will return a reading if in locked day mode, or return null if not (because the reading will be saved to the db instead)
  async createNewWeatherReading(reading: WeatherReadingCreationModel): Promise<WeatherReading> {
    reading.date = this.dateParserService.removeOffsetFromLuxonDate(reading.date);
    reading.dailyLogDate = this.dateParserService.removeOffsetFromLuxonDate(reading.dailyLogDate);

    const url = `${this.config.apiUrl}api/weatherreading/createnew`;
    const readingClone = cloneDeep(reading);
    const result = await this.http.post<WeatherReading>(url, readingClone).toPromise();

    return await this.dateParserService.deserializeDates(result, true, this.timeZoneService);
  }

  saveWeatherRemark(editor: WeatherRemarkEditor): Promise<number> {
    editor.date = this.dateParserService.removeOffsetFromLuxonDate(editor.date);

    const url = `${this.config.apiUrl}api/weatherreading/saveweatherremark`;

    return this.http
      .post<number>(url, editor)
      .toPromise()
      .then((id: number) => {
        return id;
      });
  }

  async quickSaveWeatherReadings(readings: WeatherReading[]) {
    for (let i = 0; i < readings.length; i++) {
      const rowClone = readings[i];

      rowClone.date = this.dateParserService.removeOffsetFromLuxonDate(rowClone.date);
    }

    const url = `${this.config.apiUrl}api/weatherreading/quicksaverecords`;
    let result = await this.http
      .post<WeatherReading[]>(url, readings)
      .toPromise();

    result = await this.dateParserService.deserializeDates(result, true, this.timeZoneService);

    return result;

  }

}



// WEBPACK FOOTER //
// ./src/services/weather-readings/weather-readings.service.ts